import { getAddressFieldsConfig } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import _ from 'lodash';
import { compose } from 'redux';

import { YEAR_MAX_LENGTH } from 'constants/fieldLengths';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { helocPurposeOptions, PurposeOfChattel } from 'constants/loanApplication';
import {
  chattelHomeConditionOptions,
  creditRatingOptions,
  downPaymentSourceOptions,
  loanPurposeOptions,
  manufacturedHomeTypeOptions,
  occupancyOptions,
  propertyTypeOptions,
  purposeOfChattelOptions,
  sitePlacementOptions,
  suffixOptions,
} from 'constants/options';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import {
  checkIsAdditionalLoanFlow,
  checkIsChattelLoanPurpose,
  checkIsPurchaseFlow,
  checkIsRefinanceCashOutSubflow,
  checkIsRefinanceFlow,
  getRefinancePurposeFilteredOptions,
} from 'utils/loanApplication';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { getChattelYearBuiltField } from 'utils/validation';

import { LeadTestIds } from '../../Lead.testIds';

// Value forced into the Street Address field when lead-creation fields are locked for a Purchase.
export const LOCKED_STREET_ADDRESS_VALUE = 'TBD';

export const leadFormFields = {
  ...getAddressFieldsConfig({
    prefix: 'property',
  }),
  firstName: {
    fieldId: 'firstName',
    type: FieldType.INPUT,
    nameInModel: 'firstName',
    inputProps: {
      placeholder: 'First Name',
      label: 'First Name',
      required: true,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_FIRST_NAME_INPUT,
    },
  },
  middleName: {
    fieldId: 'middleName',
    type: FieldType.INPUT,
    nameInModel: 'middleName',
    inputProps: {
      placeholder: 'Middle Name',
      label: 'Middle Name',
      required: false,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_MIDDLE_NAME_INPUT,
    },
  },
  lastName: {
    fieldId: 'lastName',
    type: FieldType.INPUT,
    nameInModel: 'lastName',
    inputProps: {
      placeholder: 'Last Name',
      label: 'Last Name',
      required: true,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_LAST_NAME_INPUT,
    },
  },
  suffix: {
    fieldId: 'suffix',
    type: FieldType.SELECT,
    nameInModel: 'suffix',
    inputProps: {
      options: suffixOptions,
      placeholder: 'Suffix',
      label: 'Suffix',
      required: false,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_SUFFIX_SELECT,
    },
  },
  emailAddress: {
    fieldId: 'emailAddress',
    type: FieldType.INPUT,
    nameInModel: 'emailAddress',
    inputProps: {
      type: FieldInputType.EMAIL,
      placeholder: 'Email Address',
      label: 'Email Address',
      required: true,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_EMAIL_INPUT,
    },
  },
  phoneNumber: {
    fieldId: 'phoneNumber',
    type: FieldType.TEL,
    nameInModel: 'phoneNumber',
    inputProps: {
      autoComplete: 'tel-extension',
      type: FieldInputType.TEL_EXT,
      placeholder: 'Phone Number',
      label: 'Phone Number',
      required: true,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_PHONE_INPUT,
    },
  },
  creditRating: {
    fieldId: 'creditRating',
    type: FieldType.SELECT,
    nameInModel: 'creditRating',
    inputProps: {
      options: creditRatingOptions,
      withSortedOptions: false,
      placeholder: 'Choose Credit Rating',
      label: 'Credit Rating',
      required: false,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_CREDIT_RATING_SELECT,
    },
  },
  isFirstTimeHomeBuyer: {
    fieldId: 'isFirstTimeHomeBuyer',
    type: FieldType.CHECKBOX,
    nameInModel: 'isFirstTimeHomeBuyer',
    inputProps: {
      label: 'Is First Time Home Buyer',
      required: false,
      variant: 'outlined',
    },
  },
  loanPurpose: {
    fieldId: 'loanPurpose',
    type: FieldType.SELECT,
    nameInModel: 'loanPurpose',
    inputProps: {
      options: loanPurposeOptions,
      placeholder: 'Choose Loan Purpose',
      label: 'Loan Purpose',
      required: false,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_LOAN_PURPOSE_SELECT,
    },
  },
  propertyType: {
    fieldId: 'propertyType',
    type: FieldType.SELECT,
    nameInModel: 'propertyType',
    inputProps: {
      options: propertyTypeOptions,
      placeholder: 'Choose Property Type',
      label: 'Property Type',
      required: false,
      variant: 'outlined',
    },
  },
  purchasePrice: {
    fieldId: 'purchasePrice',
    type: FieldType.INPUT,
    nameInModel: 'propertyValue',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Purchase Price',
      label: 'Purchase Price',
      required: false,
      decimalScale: 0,
      variant: 'outlined',
      minValue: 1,
    },
  },
  loanDownPayment: {
    fieldId: 'loanDownPayment',
    type: FieldType.INPUT,
    nameInModel: 'loanDownPayment',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Down Payment',
      label: 'Down Payment',
      required: false,
      decimalScale: 0,
      variant: 'outlined',
    },
  },
  // UI-only: never persisted, derived from/derives loanDownPayment. See LeadInformationForm.js's
  // sync effect; excluded from every submit payload and from pending-changes/dirty tracking.
  loanDownPaymentPercent: {
    fieldId: 'loanDownPaymentPercent',
    type: FieldType.INPUT,
    nameInModel: 'loanDownPaymentPercent',
    noObserve: true,
    inputProps: {
      type: FieldInputType.PERCENTAGE_RESTYLED,
      placeholder: 'Down Payment %',
      label: 'Down Payment %',
      required: false,
      decimalScale: 3,
      decimalSeparator: '.',
      fixedDecimalScale: true,
      isNumericString: true,
      variant: 'outlined',
    },
  },
  downPaymentSource: {
    fieldId: 'downPaymentSource',
    type: FieldType.SELECT,
    nameInModel: 'downPaymentSource',
    inputProps: {
      placeholder: 'Choose Down Payment Source',
      label: 'Down Payment Source',
      required: false,
      variant: 'outlined',
      options: downPaymentSourceOptions,
    },
  },
  helocPurpose: {
    fieldId: 'helocPurpose',
    type: FieldType.SELECT,
    nameInModel: 'helocPurpose',
    inputProps: {
      placeholder: 'Choose Purpose of Subordinate Financing',
      label: 'Purpose of Subordinate Financing',
      required: false,
      variant: 'outlined',
      options: helocPurposeOptions,
    },
  },
  leadSourceId: {
    fieldId: 'leadSourceId',
    type: FieldType.SELECT,
    nameInModel: 'leadSourceId',
    inputProps: {
      placeholder: 'Choose Lead Source',
      label: 'Lead Source',
      required: false,
      variant: 'outlined',
    },
  },
  loanAmount: {
    fieldId: 'loanAmount',
    type: FieldType.INPUT,
    nameInModel: 'loanAmount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Loan Amount',
      label: 'Loan Amount',
      required: false,
      decimalScale: 0,
      variant: 'outlined',
      minValue: 1,
    },
  },
  propertyValue: {
    fieldId: 'propertyValue',
    type: FieldType.INPUT,
    nameInModel: 'propertyValue',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Property Value',
      label: 'Property Value',
      required: false,
      decimalScale: 0,
      variant: 'outlined',
      minValue: 1,
    },
  },
  refinancePurpose: {
    fieldId: 'refinancePurpose',
    type: FieldType.SELECT,
    nameInModel: 'refinancePurpose',
    inputProps: {
      options: getRefinancePurposeFilteredOptions(),
      placeholder: 'Choose Purpose Of Refinance',
      label: 'Purpose Of Refinance',
      required: false,
      variant: 'outlined',
    },
  },
  loanCashOutAmount: {
    fieldId: 'loanCashOutAmount',
    type: FieldType.INPUT,
    nameInModel: 'loanCashOutAmount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Requested Cash Out Amount',
      label: 'Requested Cash Out Amount',
      required: false,
      decimalScale: 0,
      variant: 'outlined',
    },
  },
  occupancyType: {
    fieldId: 'occupancyType',
    type: FieldType.SELECT,
    nameInModel: 'occupancyType',
    inputProps: {
      options: occupancyOptions,
      placeholder: 'Choose Occupancy',
      label: 'Occupancy',
      required: false,
      variant: 'outlined',
    },
  },
  // LEN-133: a processor keys a chattel (manufactured home) application in from an email -- the home
  // basics and its proposed location go to the loan's ChattelDetails on Create Loan / Convert to Loan.
  purposeOfChattel: {
    fieldId: 'purposeOfChattel',
    type: FieldType.SELECT,
    nameInModel: 'chattelDetails.purposeOfChattel',
    inputProps: {
      options: purposeOfChattelOptions,
      placeholder: 'Choose Chattel Purpose',
      label: 'Chattel Purpose',
      required: false,
      variant: 'outlined',
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_CHATTEL_PURPOSE_SELECT,
    },
  },
  homeValue: {
    fieldId: 'homeValue',
    type: FieldType.INPUT,
    nameInModel: 'propertyValue',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Home Value / Sales Price',
      label: 'Home Value / Sales Price',
      required: false,
      decimalScale: 0,
      variant: 'outlined',
      minValue: 1,
    },
  },
  // LEN-199: a chattel purchase may carry its sales contract price at convert, so the loan opens
  // with an amount derived from it; left blank, the Chattel Details Sales Price sets it later.
  chattelSalesPrice: {
    fieldId: 'chattelSalesPrice',
    type: FieldType.INPUT,
    nameInModel: 'chattelDetails.salesPrice',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Sales Price',
      label: 'Sales Price',
      required: false,
      decimalScale: 2,
      variant: 'outlined',
      minValue: 1,
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_CHATTEL_SALES_PRICE_INPUT,
    },
  },
  chattelCondition: {
    fieldId: 'chattelCondition',
    type: FieldType.SELECT,
    nameInModel: 'chattelDetails.condition',
    inputProps: {
      options: chattelHomeConditionOptions,
      placeholder: 'Choose Condition',
      label: 'New / Used',
      required: false,
      variant: 'outlined',
    },
  },
  chattelHomeType: {
    fieldId: 'chattelHomeType',
    type: FieldType.SELECT,
    nameInModel: 'chattelDetails.homeType',
    inputProps: {
      options: manufacturedHomeTypeOptions,
      placeholder: 'Choose Home Type',
      label: 'Manufactured Home Type',
      required: false,
      variant: 'outlined',
    },
  },
  chattelYearBuilt: {
    fieldId: 'chattelYearBuilt',
    type: FieldType.INPUT,
    nameInModel: 'chattelDetails.yearBuilt',
    inputProps: {
      type: FieldInputType.YEAR,
      placeholder: 'Year Built',
      label: 'Year Built',
      required: false,
      maxLength: YEAR_MAX_LENGTH,
      customSchema: getChattelYearBuiltField,
      variant: 'outlined',
    },
  },
  chattelMake: {
    fieldId: 'chattelMake',
    type: FieldType.INPUT,
    nameInModel: 'chattelDetails.make',
    inputProps: {
      placeholder: 'Make',
      label: 'Make',
      required: false,
      variant: 'outlined',
    },
  },
  chattelModel: {
    fieldId: 'chattelModel',
    type: FieldType.INPUT,
    nameInModel: 'chattelDetails.model',
    inputProps: {
      placeholder: 'Model',
      label: 'Model',
      required: false,
      variant: 'outlined',
    },
  },
  chattelSitePlacement: {
    fieldId: 'chattelSitePlacement',
    type: FieldType.SELECT,
    nameInModel: 'chattelDetails.sitePlacement',
    inputProps: {
      options: sitePlacementOptions,
      placeholder: 'Choose Site Placement',
      label: 'Site Placement',
      required: false,
      variant: 'outlined',
    },
  },
  chattelLocationDescription: {
    fieldId: 'chattelLocationDescription',
    // The restyled FormGenerator has no TEXTAREA case -- a multiline INPUT is its text-area form.
    type: FieldType.INPUT,
    nameInModel: 'chattelDetails.locationDescription',
    inputProps: {
      type: FieldInputType.TEXT,
      placeholder: 'Proposed location / legal description when the home has no 911 address yet',
      label: 'Location / Legal Description',
      required: false,
      maxLength: 1000,
      multiline: true,
      rows: 3,
      variant: 'outlined',
      columns: 3,
      dataTestId: LeadTestIds.LEAD_INFORMATION_FORM_CHATTEL_LOCATION_DESCRIPTION_INPUT,
    },
  },
  isTest: {
    fieldId: 'isTest',
    type: FieldType.CHECKBOX,
    nameInModel: 'isTest',
    inputProps: {
      label: 'Test Lead',
      required: false,
      variant: 'outlined',
    },
  },
  subCompanyId: {
    fieldId: 'subCompanyId',
    type: FieldType.SELECT,
    nameInModel: 'subCompanyId',
    inputProps: {
      placeholder: 'Choose Sub Company',
      label: 'Sub Company',
      required: false,
      variant: 'outlined',
      staticOptions: [],
      withSortedOptions: true,
    },
  },
};

// LEN-133: many chattel homes have no 911/USPS address at application -- only State + County are required
// (plus the location description when there is no street); every other purpose keeps the shared rules.
const CHATTEL_OPTIONAL_ADDRESS_FIELD_IDS = ['street', 'city', 'zip'];

const getChattelAddressFieldsConfig = (addressFieldsConfig, isConvertLeadToLoan) =>
  _.mapValues(addressFieldsConfig, field => {
    if (CHATTEL_OPTIONAL_ADDRESS_FIELD_IDS.includes(field.fieldId)) {
      return { ...field, inputProps: { ...field.inputProps, required: false } };
    }

    if (field.fieldId === 'county') {
      return { ...field, inputProps: { ...field.inputProps, required: isConvertLeadToLoan } };
    }

    return field;
  });

// LEN-199: short enough to fit the Loan Amount input without truncating.
const CHATTEL_PURCHASE_LOAN_AMOUNT_PLACEHOLDER = 'From sales contract if blank';
const CHATTEL_PURCHASE_HOME_VALUE_LABEL = 'Home Value';

const LeadEditGroupKey = {
  PERSONAL_INFORMATION: 'personal-information',
  TRIMMED_PROPERTY_INFORMATION: 'trimmed-property-information',
  LOAN_INFORMATION: 'loan-information',
  PROPERTY_INFORMATION: 'property-information',
  MANUFACTURED_HOME: 'manufactured-home',
  SUB_COMPANY_AND_TEST_LEAD: 'sub-company-and-test-lead',
};

export const getLeadEditConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    loanPurpose,
    refinancePurpose,
    counties,
    propertyState,
    leadSourcesOptions,
    isCreateNewLead = false,
    isConvertLeadToLoan = false,
    isSubCompanyFieldShown,
    isCreateTestLoanAllowed,
    subCompaniesOptions,
    isLeadCreationFieldsDisabled = false,
    hasPropertyStreet = false,
    purposeOfChattel = null,
  }) => {
    const isRefinance = checkIsRefinanceFlow(loanPurpose);
    const isPurchase = checkIsPurchaseFlow(loanPurpose);
    const isAdditional = checkIsAdditionalLoanFlow(loanPurpose);
    const isChattel = checkIsChattelLoanPurpose(loanPurpose);
    // LEN-199: a chattel purchase derives its loan amount from the sales contract -- may be blank.
    const isChattelPurchase = isChattel && purposeOfChattel === PurposeOfChattel.PURCHASE;

    const isTrimmedLeadCreate = isCreateNewLead && !isConvertLeadToLoan;

    const arePurchaseAddressFieldsLocked = isLeadCreationFieldsDisabled && isPurchase;

    const filteredCounties = propertyState ? counties[propertyState] : counties.ALL_COUNTIES;
    const sharedAddressFieldsConfig = getAddressFieldsConfig({
      prefix: 'property',
      isWithCountry: false,
      isAllFieldsNonRequired: !isConvertLeadToLoan,
      filteredCounties,
      isStateRequired: true,
      isConvertLeadToLoan,
      isCountyRequired: true,
      isFieldTypeCDS: true,
      isStreetDisabled: arePurchaseAddressFieldsLocked,
      isUnitDisabled: arePurchaseAddressFieldsLocked,
    });
    const addressFieldsConfig = isChattel
      ? getChattelAddressFieldsConfig(sharedAddressFieldsConfig, isConvertLeadToLoan)
      : sharedAddressFieldsConfig;
    const isChattelConvert = isChattel && isConvertLeadToLoan;

    return {
      groups: [
        {
          type: FieldType.GROUP,
          key: LeadEditGroupKey.PERSONAL_INFORMATION,
          groupTitle: { label: 'Personal Information' },
          fields: [
            leadFormFields.firstName,
            leadFormFields.middleName,
            leadFormFields.lastName,
            leadFormFields.suffix,
            leadFormFields.emailAddress,
            leadFormFields.phoneNumber,
            ...getConditionalArray(!isTrimmedLeadCreate, () => [
              {
                ...leadFormFields.creditRating,
                inputProps: {
                  ...leadFormFields.creditRating.inputProps,
                  required: isConvertLeadToLoan,
                },
              },
              leadFormFields.isFirstTimeHomeBuyer,
            ]),
            ...getConditionalArray(isTrimmedLeadCreate, [
              {
                ...leadFormFields.leadSourceId,
                inputProps: {
                  ...leadFormFields.leadSourceId.inputProps,
                  options: leadSourcesOptions,
                },
              },
            ]),
          ],
        },
        ...getConditionalArray(isTrimmedLeadCreate, () => [
          {
            type: FieldType.GROUP,
            key: LeadEditGroupKey.TRIMMED_PROPERTY_INFORMATION,
            groupTitle: { label: 'Property Information' },
            fields: Object.values(addressFieldsConfig).filter(field => field.fieldId === 'state'),
          },
        ]),
        ...getConditionalArray(!isTrimmedLeadCreate, () => [
          {
            type: FieldType.GROUP,
            key: LeadEditGroupKey.LOAN_INFORMATION,
            groupTitle: { label: 'Loan Information' },
            fields: [
              {
                ...leadFormFields.loanPurpose,
                inputProps: {
                  ...leadFormFields.loanPurpose.inputProps,
                  options: loanPurposeOptions,
                  required: isConvertLeadToLoan,
                },
              },
              {
                ...leadFormFields.propertyType,
                inputProps: {
                  ...leadFormFields.propertyType.inputProps,
                  required: isConvertLeadToLoan,
                },
              },
              ...getConditionalArray(isRefinance || isAdditional, () => [
                {
                  ...leadFormFields.loanAmount,
                  inputProps: {
                    ...leadFormFields.loanAmount.inputProps,
                    required: isConvertLeadToLoan,
                  },
                },
                {
                  ...leadFormFields.propertyValue,
                  inputProps: {
                    ...leadFormFields.propertyValue.inputProps,
                    required: isConvertLeadToLoan,
                    disabled: isLeadCreationFieldsDisabled && isRefinance,
                  },
                },
                ...getConditionalArray(isAdditional, [
                  {
                    ...leadFormFields.helocPurpose,
                    inputProps: {
                      ...leadFormFields.helocPurpose.inputProps,
                      required: isConvertLeadToLoan,
                    },
                  },
                ]),
                ...getConditionalArray(isRefinance, [
                  {
                    ...leadFormFields.refinancePurpose,
                    inputProps: {
                      ...leadFormFields.refinancePurpose.inputProps,
                      required: isConvertLeadToLoan,
                    },
                  },
                ]),
                ...(checkIsRefinanceCashOutSubflow({ refinancePurpose })
                  ? [leadFormFields.loanCashOutAmount]
                  : []),
              ]),
              ...getConditionalArray(isChattel, () => [
                {
                  ...leadFormFields.purposeOfChattel,
                  inputProps: {
                    ...leadFormFields.purposeOfChattel.inputProps,
                    required: isConvertLeadToLoan,
                  },
                },
                {
                  ...leadFormFields.loanAmount,
                  inputProps: {
                    ...leadFormFields.loanAmount.inputProps,
                    required: isConvertLeadToLoan && !isChattelPurchase,
                    ...getConditionalObject(isChattelPurchase, {
                      placeholder: CHATTEL_PURCHASE_LOAN_AMOUNT_PLACEHOLDER,
                    }),
                  },
                },
                {
                  ...leadFormFields.homeValue,
                  inputProps: {
                    ...leadFormFields.homeValue.inputProps,
                    required: isConvertLeadToLoan,
                    // A chattel purchase keys its Sales Price separately, so this is just the home's value.
                    ...getConditionalObject(isChattelPurchase, {
                      placeholder: CHATTEL_PURCHASE_HOME_VALUE_LABEL,
                      label: CHATTEL_PURCHASE_HOME_VALUE_LABEL,
                    }),
                  },
                },
                ...getConditionalArray(isChattelPurchase, [
                  leadFormFields.chattelSalesPrice,
                  leadFormFields.loanDownPayment,
                  leadFormFields.loanDownPaymentPercent,
                ]),
              ]),
              ...getConditionalArray(isPurchase, [
                {
                  ...leadFormFields.purchasePrice,
                  inputProps: {
                    ...leadFormFields.purchasePrice.inputProps,
                    required: isConvertLeadToLoan,
                  },
                },
                {
                  ...leadFormFields.loanDownPayment,
                  inputProps: {
                    ...leadFormFields.loanDownPayment.inputProps,
                    required: isConvertLeadToLoan,
                  },
                },
                leadFormFields.loanDownPaymentPercent,
              ]),
              ...getConditionalArray(isPurchase && isConvertLeadToLoan, [
                leadFormFields.downPaymentSource,
              ]),
              {
                ...leadFormFields.leadSourceId,
                inputProps: {
                  ...leadFormFields.leadSourceId.inputProps,
                  options: leadSourcesOptions,
                },
              },
            ],
          },
          {
            type: FieldType.GROUP,
            key: LeadEditGroupKey.PROPERTY_INFORMATION,
            groupTitle: { label: 'Property Information' },
            fields: [
              ...Object.values(addressFieldsConfig),
              {
                ...leadFormFields.occupancyType,
                inputProps: {
                  ...leadFormFields.occupancyType.inputProps,
                  required: isConvertLeadToLoan,
                },
              },
              ...getConditionalArray(isChattel, () => [
                {
                  ...leadFormFields.chattelLocationDescription,
                  inputProps: {
                    ...leadFormFields.chattelLocationDescription.inputProps,
                    // No 911 address yet -> the proposed location / legal description locates the home.
                    required: isChattelConvert && !hasPropertyStreet,
                  },
                },
              ]),
            ],
          },
          ...getConditionalArray(isChattelConvert, () => [
            {
              type: FieldType.GROUP,
              key: LeadEditGroupKey.MANUFACTURED_HOME,
              groupTitle: { label: 'Manufactured Home' },
              fields: [
                leadFormFields.chattelCondition,
                leadFormFields.chattelHomeType,
                leadFormFields.chattelYearBuilt,
                leadFormFields.chattelMake,
                leadFormFields.chattelModel,
                leadFormFields.chattelSitePlacement,
              ],
            },
          ]),
        ]),
        ...getConditionalArray(isSubCompanyFieldShown || isCreateTestLoanAllowed, () => [
          {
            type: FieldType.GROUP,
            key: LeadEditGroupKey.SUB_COMPANY_AND_TEST_LEAD,
            title: { className: 'mt-24' },
            fields: [
              ...getConditionalArray(isSubCompanyFieldShown, [
                {
                  ...leadFormFields.subCompanyId,
                  inputProps: {
                    ...leadFormFields.subCompanyId.inputProps,
                    options: subCompaniesOptions,
                  },
                },
              ]),
              ...getConditionalArray(isCreateTestLoanAllowed, [leadFormFields.isTest]),
            ],
          },
        ]),
      ],
    };
  },
);
